import { useCallback, useEffect, useMemo, useState } from 'react'
import { t } from 'i18next'

import { useCheckoutApi } from '../checkout/useCheckoutApi'
import { usePassProducts } from '../passes/usePassProducts'
import { usePurchasedPasses } from '../passes/usePurchasedPasses'
import { useRefundPoints } from '../refundPoints/useRefundPoints'

import {
  AvailableStart,
  CheckoutQuote,
  CheckoutType,
  Money,
  PassProduct,
  PurchasedPass,
  SelectedSession,
  SummaryLine,
} from '../../types/booking/booking'
import { BookingRecipient } from '../../types/court/court'
import { PaymentMethod } from '../../types/payment/payment'
import { FundingSession, planFunding, wouldBeIncluded } from '../../utils/booking/funding'
import { applicablePoints } from '../../utils/booking/refundPoints'
import { newRequestId, sessionKey, toBookingItems } from '../../utils/booking/booking'

/** Whose prepaid passes fund the booking: the signed-in user, a customer's profile (manager), or nobody (guests). */
export type PassOwner = { self: true } | { profileId: string } | null

type UseBookingPricingOptions = {
  sessions: SelectedSession[]
  clubId?: number
  passOwner: PassOwner
  topUpPassProductId?: number
  recipient?: BookingRecipient
  /** Pay with the owner's refund points first (default true; management can switch it off). */
  useRefundPoints?: boolean
  /** Card payments keep a minimum charge when points pay part of the price. */
  paymentMethod?: PaymentMethod
}

export type ConfirmedPricing = {
  quote: CheckoutQuote
  lines: SummaryLine[]
  /** Price of the paid sessions and top-up. */
  total: Money
  /** Part of the total paid with refund points. */
  pointsAmount: number
  /** What is left to pay by card or offline. */
  amountDue: Money
}

const toFundingSession = (session: SelectedSession, clubId?: number): FundingSession => ({
  ...session,
  clubId,
})

const quotedLines = (sessions: SelectedSession[], quote: CheckoutQuote): SummaryLine[] =>
  sessions.map(session => {
    const quoted = quote.items.find(
      item =>
        item.courtId === session.courtId &&
        item.startTime.slice(0, 16) === session.startTime.slice(0, 16)
    )
    return {
      key: sessionKey(session),
      courtName: session.courtName,
      startTime: session.startTime,
      durationMinutes: session.durationMinutes,
      amount: quoted?.price ?? session.price,
      prepaidMinutes: quoted?.prepaidMinutes ?? 0,
    }
  })

/**
 * Prices the selection in the browser while the customer picks: single-session prices come with availability, and
 * prepaid minutes are planned from the owner's passes (loaded once) with the same rules as the server. Nothing is
 * requested per click. The server quotes once, when the booking is confirmed ({@link confirmQuote}), and that quote is
 * what the confirmation step shows.
 */
export const useBookingPricing = ({
  sessions,
  clubId,
  passOwner,
  topUpPassProductId,
  recipient,
  useRefundPoints: spendPoints = true,
  paymentMethod = PaymentMethod.CARD,
}: UseBookingPricingOptions) => {
  const passesApi = usePurchasedPasses()
  const productsApi = usePassProducts()
  const pointsApi = useRefundPoints()
  const [availablePoints, setAvailablePoints] = useState(0)
  const { quote: requestQuote } = useCheckoutApi()
  const [passes, setPasses] = useState<PurchasedPass[]>([])
  const [products, setProducts] = useState<PassProduct[]>([])
  const [passesLoaded, setPassesLoaded] = useState(passOwner === null)
  const [productsLoaded, setProductsLoaded] = useState(false)
  const [quoting, setQuoting] = useState(false)
  const ownerKey = passOwner === null ? '' : 'self' in passOwner ? 'self' : passOwner.profileId

  useEffect(() => {
    let active = true
    setPasses([])
    if (passOwner === null) {
      setPassesLoaded(true)
      return
    }
    setPassesLoaded(false)
    const request = 'self' in passOwner ? passesApi.mine() : passesApi.ofProfile(passOwner.profileId)
    request
      .then(result => {
        if (active) {
          setPasses(result ?? [])
        }
      })
      .catch(() => {
        // Without passes the selection is priced per session; the server quote on confirmation stays authoritative.
      })
      .finally(() => {
        if (active) {
          setPassesLoaded(true)
        }
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerKey])

  useEffect(() => {
    let active = true
    setAvailablePoints(0)
    if (passOwner === null) {
      return
    }
    const request = 'self' in passOwner ? pointsApi.summary() : pointsApi.summaryOfProfile(passOwner.profileId)
    request
      .then(result => {
        if (active) {
          setAvailablePoints(result?.available ?? 0)
        }
      })
      .catch(() => {
        // Priced without points; the server quote on confirmation stays authoritative.
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerKey])

  useEffect(() => {
    // Products are only needed for top-ups, which only pass holders are offered.
    if (passes.length === 0 || products.length > 0) {
      return
    }
    let active = true
    productsApi
      .list()
      .then(result => {
        if (active) {
          setProducts(result ?? [])
        }
      })
      .catch(() => {
        // No top-up offers without products.
      })
      .finally(() => {
        if (active) {
          setProductsLoaded(true)
        }
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passes.length])

  const plan = useMemo(
    () =>
      planFunding(
        sessions.map(session => toFundingSession(session, clubId)),
        passes,
        products,
        topUpPassProductId
      ),
    [sessions, clubId, passes, products, topUpPassProductId]
  )

  const lines: SummaryLine[] = useMemo(
    () =>
      sessions.map(session => {
        const funding = plan.items.find(item => sessionKey(item.session) === sessionKey(session))
        return {
          key: sessionKey(session),
          courtName: session.courtName,
          startTime: session.startTime,
          durationMinutes: session.durationMinutes,
          amount: session.price,
          prepaidMinutes: funding?.prepaid ? session.durationMinutes : 0,
        }
      }),
    [sessions, plan]
  )

  /** A start the owner's remaining prepaid minutes would cover, labelled "Pre-paid" (selected ones: covered by the plan). */
  const isIncluded = useCallback(
    (courtId: number, start: AvailableStart, durationMinutes: number) => {
      if (passes.length === 0) {
        return false
      }
      const key = sessionKey({ courtId, startTime: start.startTime })
      const planned = plan.items.find(item => sessionKey(item.session) === key)
      if (planned) {
        return planned.prepaid
      }
      return wouldBeIncluded(
        { courtId, clubId, startTime: start.startTime, endTime: start.endTime, durationMinutes },
        passes,
        plan.remaining
      )
    },
    [passes, plan, clubId]
  )

  const pointsAmount = spendPoints
    ? applicablePoints(plan.total?.amount ?? 0, availablePoints, paymentMethod)
    : 0
  const amountDue: Money | undefined = plan.total
    ? { ...plan.total, amount: Math.round((plan.total.amount - pointsAmount) * 100) / 100 }
    : undefined

  /** Server quote of the selection, requested once when the customer confirms. */
  const confirmQuote = useCallback(async (): Promise<ConfirmedPricing> => {
    setQuoting(true)
    try {
      const quote = await requestQuote({
        clientRequestId: newRequestId(),
        type: CheckoutType.BOOKING,
        items: toBookingItems(sessions),
        usePasses: passOwner !== null,
        topUpPassProductId: topUpPassProductId ?? null,
        recipient,
        paymentMethod,
        useRefundPoints: spendPoints,
      })
      if (!quote) {
        throw new Error(t('exceptions:quote-unavailable'))
      }
      return {
        quote,
        lines: quotedLines(sessions, quote),
        total: quote.total,
        pointsAmount: quote.pointsAmount,
        amountDue: quote.amountDue,
      }
    } finally {
      setQuoting(false)
    }
  }, [requestQuote, sessions, passOwner, topUpPassProductId, recipient, paymentMethod, spendPoints])

  return {
    lines,
    total: plan.total,
    /** Refund points the selection would spend, and what is left to pay. */
    pointsAmount,
    amountDue,
    availablePoints,
    plan,
    ready: passesLoaded && (passes.length === 0 || productsLoaded),
    isIncluded,
    confirmQuote,
    quoting,
  }
}

